import { observer } from 'mobx-react-lite'
import { BuildingOfficeIcon, UserIcon } from '@phosphor-icons/react'
import { ReactElement, useCallback, useState } from 'react'
import ComboAsync from 'app/components/ui/combo/combo-async'
import { Button } from 'app/components/ui/button'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table'
import { gqlFetchList, gqlQuery } from 'app/lib/graphqlClient'
import { opsClient } from 'app/lib/opsUrqlClient'
import { graphql } from 'app/generated/gql'
import { OptionType } from 'types/OptionType'
import { ToggleLabelCell, ToggleSwitch } from './ToggleCells'
import { InternalOnlySwitch } from './InternalOnlySwitch'
import { buildTargetSearch, type TargetRef, type TargetRow } from './featureToggleModel'

type Props = {
  target: TargetRef | null
  onSelectTarget: (t: TargetRef | null) => void
  rows: TargetRow[]
  onToggleTarget: (row: TargetRow, next: boolean) => void | Promise<void>
}

const EMPTY_OPTION: OptionType = { id: ``, name: ``, value: `` }

// The user picker uses the super-only ops `opsUsers` query, which searches every user
// across all orgs and workspaces (TRK-6058). The scoped `users` list hides org and super
// users while the caller sits in a workspace, so a super granting a toggle override could
// not find internal staff. Workspaces still use the standard scoped list.
const OPS_USERS_QUERY = graphql(`
  query FeatureToggleOpsUsers($search: String) {
    opsUsers(search: $search) {
      hits {
        id
        name
      }
    }
  }
`)

const optionsMapper = (res: { hits?: Array<{ id: string; name: string }> }): OptionType[] =>
  res.hits?.map((i) => ({ id: i.id, name: i.name, value: i.id })) ?? []

const KINDS: Array<TargetRef[`kind`]> = [`Workspace`, `User`]

export const ByTargetView = observer(function ByTargetView({
  target,
  onSelectTarget,
  rows,
  onToggleTarget,
}: Props): ReactElement {
  const [kind, setKind] = useState<TargetRef[`kind`]>(`Workspace`)
  const [internalOnly, setInternalOnly] = useState(false)
  const fetcher = useCallback(
    (params: { query: string }) => {
      if (kind === `User`) {
        return gqlQuery(
          OPS_USERS_QUERY,
          { search: buildTargetSearch(kind, params.query, internalOnly) },
          { client: opsClient },
        ).then((res) => ({ hits: res.data?.opsUsers.hits ?? [] }))
      }
      return gqlFetchList<{ id: string; name: string }>(
        `workspaces`,
        { search: buildTargetSearch(kind, params.query, internalOnly) },
        `id name`,
      )
    },
    [kind, internalOnly],
  )

  if (!target) {
    return (
      <div className="max-w-md space-y-3">
        <div className="flex gap-2">
          {KINDS.map((k) => (
            <Button key={k} size="sm" variant={kind === k ? `secondary` : `tertiary`} onClick={() => setKind(k)}>
              {k === `Workspace` ? (
                <BuildingOfficeIcon className="mr-1.5 h-4 w-4" />
              ) : (
                <UserIcon className="mr-1.5 h-4 w-4" />
              )}
              {k}
            </Button>
          ))}
        </div>
        {kind === `User` && <InternalOnlySwitch checked={internalOnly} onCheckedChange={setInternalOnly} />}
        <ComboAsync
          key={`${kind}-${internalOnly}`}
          fetcher={fetcher}
          optionsMapper={optionsMapper}
          value={EMPTY_OPTION}
          onChange={(opt) => opt.id && onSelectTarget({ kind, id: opt.id, name: opt.name })}
          placeholder={`Search ${kind.toLowerCase()}`}
          showSearchInDropdown
          prefetch
        />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <span className="text-sm text-subtle">{target.kind}:</span>
        <span className="text-sm font-semibold">{target.name}</span>
        <Button size="sm" variant="tertiary" onClick={() => onSelectTarget(null)}>
          change
        </Button>
      </div>
      <div className="overflow-hidden rounded-md border border-default bg-surface shadow-elevation-1">
        <table className="w-full caption-bottom table-fixed text-sm">
          <colgroup>
            <col />
            <col className="w-36" />
            <col className="w-24" />
          </colgroup>
          <TableHeader>
            <TableRow className="bg-surface-subtle hover:bg-surface-subtle">
              <TableHead className="px-4 text-ui-xs font-bold text-default">Toggle</TableHead>
              <TableHead className="px-4 text-ui-xs font-bold text-default">State</TableHead>
              <TableHead className="px-4 text-right text-ui-xs font-bold text-default">Enabled</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-subtle">
            {rows.map((row) => {
              const isGlobal = row.state.kind === `global`
              const checked = row.state.kind !== `off`
              const stateLabel =
                row.state.kind === `global` ? `On (global)` : row.state.kind === `override` ? `On (override)` : `Off`
              return (
                <TableRow key={row.name}>
                  <TableCell className="max-w-0 whitespace-normal px-4 py-3 align-middle">
                    <ToggleLabelCell label={row.label} slug={row.name} description={row.description} />
                  </TableCell>
                  <TableCell
                    className={`px-4 py-3 align-middle text-xs ${row.state.kind === `off` ? `text-minimal` : `text-subtle`}`}
                  >
                    {stateLabel}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right align-middle">
                    <div className="flex justify-end">
                      <ToggleSwitch
                        label={row.label}
                        checked={checked}
                        disabled={isGlobal}
                        disabledReason={isGlobal ? `Enabled globally — manage in By Feature` : undefined}
                        onCheckedChange={(next) => onToggleTarget(row, next)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </table>
      </div>
    </div>
  )
})
